<template>
	<form class="wide" @submit.prevent="submit">
		<loader v-if="!formReady"/>
		<template v-else>
			<fieldset>
				<div>
					<div>
						<legend><span>Panel Details</span></legend>
					</div>
					<div>
						<label><span>Name </span>
							<div>
								<input v-model="form.data.name" type="text">
							</div>
							<div v-if="form.errors.get('name')" class="error-block">
								<p v-html="form.errors.get('name')"></p>
							</div>
						</label>
						<label><span>Service </span>
							<div>
								<select v-model="form.data.service">
									<option :value="null" disabled selected>—</option>
									<option
										v-for="(service, serviceIndex) in form.options.services"
										:key="serviceIndex"
										:value="service.id"
										v-html="$present('ucWords', service.key)"/>
								</select>
							</div>
							<div v-if="form.errors.get('service')" class="error-block">
								<p v-html="form.errors.get('service')"></p>
							</div>
						</label>
						<template v-if="service">
							<label class="checkbox"><span>Shared panel </span>
								<input v-model="form.data.shared" type="checkbox">
							</label>
							<template v-if="!form.data.shared">
								<label><span>Introducer </span>
									<div
										v-if="!viableIntroducers.length"
										class="nothing-to-see-here">
										<p>No service providers currently introducing this service</p>
									</div>
									<select v-else v-model="form.data.introducer">
										<option :value="null" disabled selected>—</option>
										<option
											v-for="(serviceProvider, serviceProviderIndex) in viableIntroducers"
											:key="serviceProviderIndex"
											:value="serviceProvider.id"
											v-html="serviceProvider.profile.name"/>
									</select>
									<div v-if="form.errors.get('introducer')" class="error-block">
										<p v-html="form.errors.get('introducer')"></p>
									</div>
								</label>
								<label class="checkbox"><span>Does the introducer create private clients? </span>
									<input
										v-model="form.data.private" type="checkbox">
								</label>
							</template>
						</template>
					</div>
				</div>
			</fieldset>
			<template v-if="service">
				<fieldset>
					<div>
						<div>
							<legend><span>Panel Configuration</span></legend>
						</div>
						<div>
							<label><span>Service Providers </span>
								<div
									v-if="!viableServiceProviders.length"
									class="nothing-to-see-here">
									<p>No service providers currently offering this service</p>
								</div>
								<checkbox-manager
									v-else
									:all-items="viableServiceProviders"
									:checked="form.optionsByFieldArray('serviceProviders', 'service_providers')"
									helpBlock="Move the service providers that <em>will be included on this panel </em>into the right hand column or vice versa."
									itemLabel="profile.name"
									v-on:valuesChanged="(items) => form.set('service_providers', items)"/>
							</label>
							<label
								v-if="service.comparable"
								class="checkbox"><span>Generate leads for matched service providers </span>
								<div>
									<input v-model="form.data.create_leads" type="checkbox">
								</div>
							</label>
							<label v-if="service.quotable" class="checkbox"><span>Generate a single quote </span>
								<div>
									<input v-model="form.data.single_quote" type="checkbox">
								</div>
							</label>
						</div>
					</div>
				</fieldset>
				<fieldset>
					<div>
						<div>
							<legend>
								<span>Commissions</span>
							</legend>
						</div>
						<div>
							<label v-if="!service.commission_type"><span>Commission Type </span>
								<select v-model="form.data.commission_type">
									<option :value="null">—</option>
									<option value="price">Fixed fee</option>
									<option value="percent">Percent</option>
								</select>
								<div v-if="form.errors.get('commission_type')" class="error-block">
									<p v-html="form.errors.get('commission_type')"></p>
								</div>
							</label>
							<label
								v-if="form.data.commission_type === 'price'"><span>Calm instruction commission (Fixed £) </span>
								<input
									v-model="form.data.calm_commission_price"
									type="number">
								<div v-if="form.errors.get('calm_commission_price')" class="error-block">
									<p v-html="form.errors.get('calm_commission_price')"></p>
								</div>
							</label>
							<label
								v-if="form.data.commission_type === 'percent'"><span>Calm instruction commission (Percent %) </span>
								<input
									v-model="form.data.calm_commission_percent"
									type="number">
								<div v-if="form.errors.get('calm_commission_percent')" class="error-block">
									<p v-html="form.errors.get('calm_commission_percent')"></p>
								</div>
							</label>
						</div>
					</div>
				</fieldset>
				<template v-if="service.quotable && form.data.single_quote">
					<fieldset>
						<div>
							<div>
								<legend><span>Conveyancing Fees</span></legend>
							</div>
							<div>
								<div class="fee-fields">
									<label class="input-group"><span>Sale Transaction Value Fees </span>
										<div>
											<div
												v-for="(data, index) in form.data.fees.conveyancing.sale.fee_transaction_bands"
												class="input-grid--fees input-grid--bands">
												<label class="limit"><span>Up To (k) </span>
													<div
														v-if="index < form.data.fees.conveyancing.sale.fee_transaction_bands.length - 1">
														<input
															v-model="form.data.fees.conveyancing.sale.fee_transaction_bands[index].upto"
															step="1"
															type="number">
													</div>
												</label>
												<label class="fee"><span>Fee </span>
													<div>
														<input
															v-model="form.data.fees.conveyancing.sale.fee_transaction_bands[index].fee"
															step="1"
															type="number">
													</div>
												</label>
												<label class="repeater-controls">
													<button
														class="repeater-controls__add"
														@click.prevent="addFeeBand('sale', index)">Add
													</button>
													<button
														class="repeater-controls__remove"
														@click.prevent="removeFeeBand('sale', index)">Remove
													</button>
												</label>
											</div>
										</div>
									</label>
									<label class="input-group"><span>Purchase Transaction Value Fees </span>
										<div>
											<div
												v-for="(data, index) in form.data.fees.conveyancing.purchase.fee_transaction_bands"
												class="input-grid--fees input-grid--bands">
												<label class="limit"><span>Up To (k) </span>
													<div
														v-if="index < form.data.fees.conveyancing.purchase.fee_transaction_bands.length - 1">
														<input
															v-model="form.data.fees.conveyancing.purchase.fee_transaction_bands[index].upto"
															step="1"
															type="number">
													</div>
												</label>
												<label class="fee"><span>Fee </span>
													<div>
														<input
															v-model="form.data.fees.conveyancing.purchase.fee_transaction_bands[index].fee"
															step="1"
															type="number">
													</div>
												</label>
												<label class="repeater-controls">
													<button
														class="repeater-controls__add"
														@click.prevent="addFeeBand('purchase', index)">Add
													</button>
													<button
														class="repeater-controls__remove"
														@click.prevent="removeFeeBand('purchase', index)">Remove
													</button>
												</label>
											</div>
										</div>
									</label>
									<label
										class="input-group"><span>Remortgage Transaction Value Fees </span>
										<div>
											<div
												v-for="(data, index) in form.data.fees.conveyancing.remortgage.fee_transaction_bands"
												class="input-grid--fees input-grid--bands">
												<label
													class="limit"><span>Up To (k) </span>
													<div
														v-if="index < form.data.fees.conveyancing.remortgage.fee_transaction_bands.length - 1">
														<input
															v-model="form.data.fees.conveyancing.remortgage.fee_transaction_bands[index].upto"
															step="1"
															type="number">
													</div>
												</label>
												<label
													class="fee"><span>Fee </span>
													<div>
														<input
															v-model="form.data.fees.conveyancing.remortgage.fee_transaction_bands[index].fee"
															step="1"
															type="number">
													</div>
												</label>
												<label class="repeater-controls">
													<button
														class="repeater-controls__add"
														@click.prevent="addFeeBand('remortgage', index)">Add
													</button>
													<button
														class="repeater-controls__remove"
														@click.prevent="removeFeeBand('remortgage', index)">Remove
													</button>
												</label>
											</div>
										</div>
									</label>
								</div>
								<div class="fee-fields">
									<label class="input-group"><span>Telegraphic Transfer Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_telegraphic_transfer"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_telegraphic_transfer"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.fee_telegraphic_transfer"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<label
										class="input-group"><span>Leasehold Property Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_leasehold_property"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_leasehold_property"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.fee_leasehold_property"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<label
										class="input-group"><span>Islamic Mortgage Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_islamic_mortgage"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_islamic_mortgage"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.fee_islamic_mortgage"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<label
										class="input-group"><span>New Build Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_new_build"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Shared Ownership Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_shared_ownership"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_shared_ownership"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.fee_shared_ownership"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Right To Buy Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_right_to_buy"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Sale </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Transfer Of Equity Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_transfer_of_equity"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_transfer_of_equity"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.fee_transfer_of_equity"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Auction/Repossession Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_auction_repossession"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_auction_repossession"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Help To Buy Scheme Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_help_to_buy_scheme"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_help_to_buy_scheme"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Help To Buy ISA Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_help_to_buy_isa"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_help_to_buy_isa"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Stamp Duty Form Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_stamp_duty_form"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Mortgage Admin Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_mortgage_admin"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_mortgage_admin"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Lawyer Checker Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_lawyer_checker"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_lawyer_checker"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.fee_lawyer_checker"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<label class="input-group"><span>File Admin Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_file_admin"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_file_admin"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.fee_file_admin"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Lender Monitor Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.fee_lender_monitor"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.fee_lender_monitor"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.fee_lender_monitor"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
								</div>
							</div>
						</div>
					</fieldset>
					<fieldset>
						<div>
							<div>
								<legend><span>Conveyancing Disbursements</span></legend>
							</div>
							<div>
								<div class="fee-fields">
									<label class="input-group"><span>Search Pack </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.disbursement_search_pack"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Bankruptcy Search Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.disbursement_bankruptcy_search"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.disbursement_bankruptcy_search"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<label class="input-group"><span>ID Check Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.disbursement_id_check"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.disbursement_id_check"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.disbursement_id_check"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<label class="input-group"><span>Office Copies & Title Plan Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.sale.disbursement_office_copies_title_plan"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="purchase"><span>Purchase </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.disbursement_office_copies_title_plan"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<label
										class="input-group"><span>Land Registry Pre Completion Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
											<label class="purchase"><span>Purchase </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.purchase.disbursement_land_registry_pre_completion"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.disbursement_land_registry_pre_completion"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<label
										class="input-group"><span>Remortgage No Search Insurance Fee </span>
										<div class="input-grid--fees">
											<label class="sale"><span>Sale </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
											<label class="purchase"><span>Purchase </span>
												<p class="not-applicable">Not Applicable</p>
											</label>
											<label class="remortgage"><span>Remortgage </span>
												<div>
													<input
														v-model="form.data.fees.conveyancing.remortgage.disbursement_remortgage_search_policy"
														min="0.01" step="0.01"
														type="number"/>
												</div>
											</label>
										</div>
									</label>
									<div class="fieldset-section">
										<header>
											<h2>Other Disbursements</h2>
										</header>
										<label class="inline"><span>ID Check Fee</span>
											<input
												v-model="form.data.fees.conveyancing.other.disbursement_id_check"
												class="number--hundreds" min="0.01" step="0.01"
												type="number"/>
										</label>
									</div>
								</div>
							</div>
						</div>
					</fieldset>
				</template>
				<fieldset v-if="method === 'post' && selectedIntroducer">
					<div>
						<div>
							<legend><span>Apply Panel</span></legend>
						</div>
						<div>
							<label
								class="checkbox"><span>Apply this panel to the {{ service.names.singular }} introduction config for {{ selectedIntroducer.profile.name }} as it is created? </span>
								<input
									v-model="form.data.apply_panel" type="checkbox">
							</label>
						</div>
					</div>
				</fieldset>
			</template>
			<div class="actions">
				<ul>
					<li>
						<process-button
							:processing="form.processing" :text="$attrs.cta" class="button button--primary"/>
					</li>
				</ul>
			</div>
		</template>
	</form>
</template>
<script>
import panelFormFields from "@/constants/panelFormFields";
import {createNamespacedHelpers} from "vuex";

const {mapActions} = createNamespacedHelpers("panels");

export default {

	data() {
		return {
			formReady: false,
			form: this.newForm(panelFormFields),
		};
	},

	computed: {
		service() {
			if (!this.form.data.service) {
				return null;
			}
			return _.find(this.form.options.services, service => {
				return service.id === this.form.data.service;
			});
		},
		selectedIntroducer() {
			if (this.form.data.shared) {
				return null;
			}
			return _.find(this.form.options.introducers, introducer => {
				return introducer.id === this.form.data.introducer;
			}) || null;
		},
		viableIntroducers() {
			return _.filter(this.form.options.introducers, provider => {
				return _.find(provider.referredServices, service => {
					return service.id === this.service.id;
				});
			});
		},
		viableServiceProviders() {
			return _.filter(this.form.options.serviceProviders, provider => {
				return _.find(provider.providedServices, service => {
					return service.id === this.service.id;
				});
			});
		},
	},

	props: {
		panel: null,
		method: {
			type: String,
			default() {
				return "post";
			},
		},
	},

	methods: {
		...mapActions([
			"addModel",
			"updateModel",
		]),
		addFeeBand(type, index) {
			this.form.pushDefault("fees.conveyancing." + type + ".fee_transaction_bands", index);
		},
		removeFeeBand(type, index) {
			this.form.removeIndex("fees.conveyancing." + type + ".fee_transaction_bands", index);
		},
		async submit() {
			if ("post" === this.method) {
				this.form.post("panels").then(response => {
					this.$router.push({
						name: "panels.edit",
						params: {
							panel: response.data.id,
						},
					});
				});
			}

			if ("patch" === this.method) {
				this.form.patch("panels/" + this.panel.id).then(data => {
					this.updateModel(data.data);
					this.$store.dispatch("alertsStore/success", "Panel Updated");
				});
			}
		},
	},

	watch: {
		"form.data.service": {
			handler: function () {
				this.form.set("commission_type", this.service.commission_type);
			},
		},
	},

	created() {
		http().get(this.route("form-data/panel/:panel", this.panel)).then(response => {
			this.form.setOptions(response.data);

			this.form.pushDefault("fees.conveyancing.sale.fee_transaction_bands");
			this.form.pushDefault("fees.conveyancing.purchase.fee_transaction_bands");
			this.form.pushDefault("fees.conveyancing.remortgage.fee_transaction_bands");

			if (this.panel) {
				this.form.prefill(this.panel);

				this.form.set("service", this.panel.service.id);
				this.form.set("introducer", this.panel.introducer?.id);
				this.form.set("service_providers", _.map(this.panel.serviceProviders, serviceProvider => {
					return serviceProvider.id;
				}));
				this.form.set("calm_commission_price", this.panel.calmCommissionPrice?.wholes);
				this.form.set("calm_commission_percent", this.panel.calmCommissionPercent?.percent);
				// this.form.set("introducer_commission_price", this.panel.introducerCommissionPrice?.wholes);
				this.form.set("introducer_commission_percent", this.panel.introducerCommissionPercent?.percent);

				_.each(this.panel.fees, (fees, serviceKey) => {
					fees = fees.introducers[""].fees;
					let mappedFees = _.mapValues(fees, (fees, type) => {
						return _.mapValues(fees, (data, fee) => {
							if ("fee_transaction_bands" === fee) {
								if (!data.length) {
									return this.form.originalData.fees.conveyancing[type].fee_transaction_bands;
								}
								return _.map(data, (data) => {
									return {
										fee: data.fee.float,
										upto: !data.upto ? null : data.upto.wholes / 1000,
									};
								});
							}

							return data ? data.float : null;
						});
					});

					_.each(mappedFees, (mapped, type) => {
						this.form.set("fees." + serviceKey + "." + type, mapped);
					});
				});
			}

			this.formReady = true;
		});
	},
};
</script>
